import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Button,
  useTheme,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Purchase } from '../types';
import { apiService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import Breadcrumb from '../components/Common/Breadcrumb';

const Purchases: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  
  const [purchases, setPurchases] = useState<Purchase[]>([]);
  const [standalonePurchases, setStandalonePurchases] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    const fetchPurchases = async () => {
      try {
        setLoading(true);
        const [purchasesData, standaloneData] = await Promise.all([
          apiService.getMyPurchases(),
          apiService.getMyStandaloneExamPurchases().catch(() => []),
        ]);

        // Only keep verified purchases
        const verifiedCoursePurchases = Array.isArray(purchasesData)
          ? purchasesData.filter((p) => p.is_verified)
          : [];

        const verifiedStandalonePurchases = Array.isArray(standaloneData)
          ? standaloneData.filter((sp) => sp.is_verified)
          : [];

        setPurchases(verifiedCoursePurchases);
        setStandalonePurchases(verifiedStandalonePurchases);
      } catch (error) {
        console.error('Error fetching purchases:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPurchases();
  }, [isAuthenticated, navigate]);

  const getStatusColor = (isExpired: boolean, isCertified?: boolean, isActive?: boolean) => {
    if (isExpired) return 'default';
    if (isCertified) return 'success';
    if (isActive) return 'info';
    return 'info';
  };

  const getStatusText = (isExpired: boolean, isCertified?: boolean, isActive?: boolean) => {
    if (isExpired) return '⏰ Expired';
    if (isCertified) return '✅ Certified';
    if (isActive) return '📚 Active';
    return '📚 Active';
  };

  if (loading) {
    return <LoadingSpinner message="Loading your purchases..." />;
  }

  return (
    <Box sx={{ py: 8 }}>
      <Container maxWidth="lg">
        {/* Breadcrumb */}
        <Breadcrumb />
        
        <Typography
          variant="h3"
          component="h1"
          gutterBottom
          sx={{ mb: 6 }}
        >
          My Purchases
        </Typography>

        {purchases.length > 0 || standalonePurchases.length > 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {/* Course purchases */}
            {purchases.map((purchase) => (
              <Box key={`course-${purchase.id}`}>
                <Card
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    cursor: 'pointer',
                    transition: 'transform 0.3s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: theme.shadows[4],
                    },
                  }}
                  onClick={() => navigate(`/courses/${purchase.course.hashed}`)}
                >
                  <CardMedia
                    component="img"
                    sx={{
                      width: { xs: '100%', md: 200 },
                      height: { xs: 200, md: 'auto' },
                    }}
                    image={purchase.course.image}
                    alt={purchase.course.name}
                  />
                  <CardContent sx={{ flexGrow: 1, p: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Chip 
                        label={purchase.course.category.category} 
                        size="small" 
                        color="primary"
                        variant="outlined"
                      />
                      <Chip 
                        label={getStatusText(purchase.is_expired, purchase.is_certified, purchase.is_active)} 
                        size="small" 
                        color={getStatusColor(purchase.is_expired, purchase.is_certified, purchase.is_active)}
                      />
                    </Box>
                    
                    <Typography variant="h5" component="h3" gutterBottom>
                      {purchase.course.name}
                    </Typography>
                    
                    <Typography variant="body1" color="text.secondary" paragraph>
                      {purchase.course.description}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Purchased: {new Date(purchase.purchased_at).toLocaleDateString()}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Payment Method: {purchase.payment_method}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="h6" color="primary" gutterBottom>
                          ${purchase.course.price}
                        </Typography>
                        <Button
                          variant="contained"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/courses/${purchase.course.hashed}`);
                          }}
                        >
                          {purchase.is_verified ? 'Access Course' : 'View Details'}
                        </Button>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            ))}

            {/* Standalone exam purchases */}
            {standalonePurchases.map((sp) => (
              <Box key={`standalone-${sp.id}`}>
                <Card
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    cursor: 'pointer',
                    transition: 'transform 0.3s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: theme.shadows[4],
                    },
                  }}
                  onClick={() => {
                    if (sp.standalone_exam?.hashed) {
                      navigate(`/exams/${sp.standalone_exam.hashed}`);
                    }
                  }}
                >
                  <CardMedia
                    component="img"
                    sx={{
                      width: { xs: '100%', md: 200 },
                      height: { xs: 200, md: 'auto' },
                    }}
                    image={sp.standalone_exam?.thumbnail}
                    alt={sp.standalone_exam?.title}
                  />
                  <CardContent sx={{ flexGrow: 1, p: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Chip 
                        label="Standalone Exam" 
                        size="small" 
                        color="primary"
                        variant="outlined"
                      />
                      <Chip 
                        label={getStatusText(sp.is_expired, undefined, sp.is_active)} 
                        size="small" 
                        color={getStatusColor(sp.is_expired, undefined, sp.is_active)}
                      />
                    </Box>
                    
                    <Typography variant="h5" component="h3" gutterBottom>
                      {sp.standalone_exam?.title ?? 'Exam'}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Purchased: {new Date(sp.purchased_at).toLocaleDateString()}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Payment Method: {sp.payment_method}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Button
                          variant="contained"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (sp.standalone_exam?.hashed) {
                              navigate(`/exams/${sp.standalone_exam.hashed}`);
                            }
                          }}
                        >
                          {sp.is_active ? 'Access Exam' : 'View Exam'}
                        </Button>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            ))}
          </Box>
        ) : (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h5" color="text.secondary" gutterBottom>
              No purchases yet
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              Start your learning journey by purchasing one of our courses.
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/courses')}
            >
              Browse Courses
            </Button>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default Purchases;